import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { organisationAdminService } from '../services/organisationAdminService'
import { PageHeader } from '../components/ui/PageHeader'
import { SearchField } from '../components/ui/SearchField'
import { DataTable } from '../components/ui/DataTable'
import { Button } from '../components/ui/Button'

function formatDateTime(isoOrDate) {
  if (!isoOrDate) return '—'
  const d = new Date(isoOrDate)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  })
}

function formatLocations(locations) {
  if (!Array.isArray(locations) || locations.length === 0) return '—'
  return locations
    .map((loc) => [loc.city, loc.state, loc.country].filter(Boolean).join(', '))
    .filter(Boolean)
    .join(' · ')
}

function matchesSearch(org, query) {
  if (!query) return true
  const q = query.toLowerCase()
  const fields = [
    org.organisation_name,
    org.website,
    org.subscription_code,
    org.subscription_name,
    org.organisation_id,
  ]
  return fields.some((value) => value && String(value).toLowerCase().includes(q))
}

const Organisations = () => {
  const navigate = useNavigate()
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchDraft, setSearchDraft] = useState('')
  const [appliedSearch, setAppliedSearch] = useState('')

  const fetchList = useCallback(async () => {
    setLoading(true)
    try {
      const res = await organisationAdminService.listOrganisations()
      const list = Array.isArray(res?.data) ? res.data : []
      setRows(list)
    } catch {
      setRows([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchList()
  }, [fetchList])

  const filteredRows = useMemo(
    () => rows.filter((org) => matchesSearch(org, appliedSearch)),
    [rows, appliedSearch]
  )

  const columns = [
    {
      key: 'organisation_name',
      header: 'Name',
      render: (org) => <span className="font-medium text-secondary-900 dark:text-secondary-100">{org.organisation_name || '—'}</span>,
    },
    {
      key: 'website',
      header: 'Website',
      render: (org) =>
        org.website ? (
          <a
            href={org.website.startsWith('http') ? org.website : `https://${org.website}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary-600 hover:underline dark:text-primary-400"
          >
            {org.website}
          </a>
        ) : (
          '—'
        ),
    },
    {
      key: 'subscription',
      header: 'Subscription',
      cellClassName: 'whitespace-nowrap',
      render: (org) =>
        org.subscription_name
          ? `${org.subscription_name} (${org.subscription_code || '—'})`
          : org.subscription_code || '—',
    },
    {
      key: 'locations',
      header: 'Locations',
      cellClassName: 'max-w-[220px]',
      render: (org) => formatLocations(org.locations),
    },
    {
      key: 'created_at',
      header: 'Created',
      cellClassName: 'whitespace-nowrap',
      render: (org) => formatDateTime(org.created_at),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (org) => (
        <Button type="button" variant="secondary" size="sm" onClick={() => navigate(`/admin/organisations/${org.organisation_id}`)}>
          View
        </Button>
      ),
    },
  ]

  return (
    <div>
      <PageHeader
        title="Organisations"
        subtitle="Browse all organisations. Search by name, website, subscription, or ID."
      />

      <SearchField
        className="mb-4"
        value={searchDraft}
        onChange={(e) => setSearchDraft(e.target.value)}
        onSubmit={() => setAppliedSearch(searchDraft.trim())}
        onClear={searchDraft ? () => { setSearchDraft(''); setAppliedSearch('') } : undefined}
        placeholder="Organisation name, website, subscription…"
      />

      <p className="mb-4 text-sm text-text-muted dark:text-text-muted-dark">
        {filteredRows.length} organisation{filteredRows.length === 1 ? '' : 's'}
        {appliedSearch ? ` matching “${appliedSearch}”` : ''}
      </p>

      <DataTable
        columns={columns}
        data={filteredRows}
        loading={loading}
        minWidth="min-w-[960px]"
        emptyTitle="No organisations found"
        getRowKey={(org) => org.organisation_id}
      />
    </div>
  )
}

export default Organisations
